/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { AxiosRequestConfig } from 'axios';
import isEmpty from 'lodash.isempty';
import omit from 'lodash.omit';
import Moment from 'moment';
import { axiosInstance, IServerSideGetRowsParams, PaginatedApiResponse } from '@fides/fides-react-components';
import { ColDef } from '@ag-grid-community/core';

export interface ColumnMapping {
  [field: string]: string;
}

/**
 * Field names in the grid's columnDef could vary from the query parameter names
 * expected by the backend. Ag-grid's filterModel and sortModel object uses the
 * columnDef filter key which we need to convert.
 *
 * The conversion in done by a `columnMapping` object.
 *
 * Example:
 *
 * columnDefs = [
 *   ...,
 *   {
 *     field: 'masterAccount.account.name',
 *   },
 *   ...
 * ]
 *
 * const columnMapping = {
 *   'masterAccount.account.name': 'masterAccountName',
 * };
 */
const IN_RANGE_SUFFIX = 'InRange';

function setTime(upperBound?: moment.Moment, lowerBound?: moment.Moment) {
  // otherwise sends to backend prev day
  if (upperBound) {
    upperBound.set('hours', 23);
    upperBound.set('minutes', 59);
    upperBound.set('seconds', 59);
  }

  if (lowerBound) {
    lowerBound.set('hours', 23);
    lowerBound.set('minutes', 59);
    lowerBound.set('seconds', 59);
  }
}

function mapField(field: string, columnMapping: ColumnMapping = {}) {
  return columnMapping.hasOwnProperty(field) ? columnMapping[field] : field;
}

function getColNamesThatHaveFilters(colDefs: ColDef[], columnMapping: ColumnMapping = {}) {
  return colDefs
    .filter(colDef => colDef.filter !== false)
    .reduce((prev: string[], cur: ColDef) => {
      if (!cur.field) {
        return prev;
      }

      const mapped = mapField(cur.field, columnMapping);
      return [...prev, mapped];
    }, []);
}

function getColNamesRanged(colDefs: ColDef[], columnMapping: ColumnMapping = {}) {
  return colDefs
    .filter(colDef => colDef.filter !== false)
    .reduce((prev: string[], cur: ColDef) => {
      if (!cur.field) {
        return prev;
      }
      if (!cur.filterParams.filterOptions?.includes('inRange')) {
        return prev;
      }
      const mapped = mapField(cur.field, columnMapping) + IN_RANGE_SUFFIX;
      return [...prev, mapped];
    }, []);
}

function mapFilterModel(filterModel: any, columnMapping?: ColumnMapping) {
  return Object.entries(filterModel).reduce((acc, cur) => {
    const [key, value] = cur;
    let queryKey = (columnMapping && columnMapping[key]) || key;

    let queryValue =
      (value as any).filter || (value as any).filterType === 'number' // detect zero values undefined
        ? (value as any).filter
        : (value as any).values;
    // filter for numeric and dates
    if (
      value &&
      (value as any).type &&
      ((value as any).type.toLowerCase() === IN_RANGE_SUFFIX.toLowerCase() || (value as any).filterType === 'date')
    ) {
      // date options: 'inRange' && equals, formatTime

      // inRange option
      if ((value as any).type.toLowerCase() === IN_RANGE_SUFFIX.toLowerCase()) {
        queryValue =
          (value as any).filterTo || (value as any).filterType === 'number' // detect zero values undefined
            ? { lowerBound: queryValue, upperBound: (value as any).filterTo }
            : {
              lowerBound: Moment((value as any).dateFrom).utc(true),
              upperBound: Moment((value as any).dateTo).utc(true),
            };
      } else if ((value as any).filterType === 'date') {
        // equals date
        queryValue = { lowerBound: Moment((value as any).dateFrom).utc(true) };
      }

      if ((value as any).filterType === 'date') {
        setTime(queryValue.upperBound, queryValue.lowerBound);
      }

      // InRange suffix
      queryKey += IN_RANGE_SUFFIX;
    }

    // blanks are null (ex: bank) change for empty string
    if ((value as any).values) {
      queryValue = (value as any).values.map((val: any) => (val === null ? '' : val));
    }
    acc[queryKey] = queryValue;
    return acc;
  }, {} as any);
}

export function createDatasource<T>(
  initialConfig: AxiosRequestConfig,
  requestParams?: any,
  columnMapping?: ColumnMapping,
  errorHandler?: any,
  colDefs: ColDef[] = [],
) {
  return {
    async getRows(params: IServerSideGetRowsParams) {
      const gridQueryParams: AxiosRequestConfig['params'] = {};
      const { startRow = 0 } = params.request;
      const { endRow = 0 } = params.request;
      const config = { ...initialConfig };

      const withFilters = getColNamesThatHaveFilters(colDefs, columnMapping);
      const withFiltersRanged = getColNamesRanged(colDefs, columnMapping);

      const filtersToOmit = [...withFilters, ...withFiltersRanged];

      const filterParams = params.request.filterModel
        ? mapFilterModel(params.request.filterModel, columnMapping)
        : undefined;

      if (!isEmpty(filterParams)) {
        // initial filter
        if (requestParams?.filter) {
          // original filter, for grouping, range date outside grid

          config.params.filter = {
            ...omit(requestParams?.filter, filtersToOmit),
            ...filterParams,
          };
        } else {
          gridQueryParams.filter = filterParams;
        }
      } else {
        config.params = requestParams;
        config.params.filter = {
          ...omit(requestParams?.filter, filtersToOmit),
        }
      }

      if (params.request.sortModel && params.request.sortModel.length > 0) {
        gridQueryParams.sort = params.request.sortModel.map((s: any) => {
          const sortCol = (columnMapping && columnMapping[s.colId]) || s.colId;
          return `${sortCol},${s.sort}`;
        });
      }

      gridQueryParams.size = endRow - startRow;
      gridQueryParams.page = Math.ceil(endRow / gridQueryParams.size) - 1;

      config.params = {
        ...config.params,
        ...gridQueryParams,
      };

      let response;
      try {
        response = await axiosInstance
          .request<PaginatedApiResponse<T>>(config)
          .catch((error) => {
            if (errorHandler) {
              errorHandler.handleError(error.response, error.config);
            }
          });
      } catch {
        params.fail();
      }

      if (response && response.data) {
        params.success({
          rowData: response.data.content,
          rowCount: response.data.totalElements || 0
        });
      } else {
        // inform grid request failed
        params.fail();
        // display no elements
        params.success({ rowData: [], rowCount: 0 });
      }
    },
  };
}

export function useDatasource<T>(
  config: AxiosRequestConfig,
  requestParams?: any,
  columnMapping?: ColumnMapping,
  errorHandler?: any,
  colDefs?: any
) {
  return React.useMemo(
    () => createDatasource<T>(config, requestParams, columnMapping, errorHandler, colDefs),
    [config, columnMapping, requestParams, colDefs]
  );
}
